import { z } from 'zod'

export const nameSchema = (fieldName: string, maxLimit = 70, allowNumbers = false) =>
  z
    .string()
    .min(1, `${fieldName} is required`)
    .max(maxLimit, `${fieldName} cannot exceed ${maxLimit} characters`)
    .regex(
      allowNumbers ? /^[a-zA-Z0-9 '.-]+$/ : /^[a-zA-Z '.-]+$/,
      `${fieldName} can only contain letters${allowNumbers ? ', numbers' : ''}, spaces, apostrophes, hyphens, and periods`,
    )
    .refine((value) => !value.startsWith(' ') && !value.endsWith(' '), {
      message: `${fieldName} cannot start or end with spaces`,
    })

/**
 * Validation rule definition for multi-rule validation
 */
export interface ValidationRule<T = string> {
  /** Function that returns true if value is valid, false if invalid */
  test: (value: T, context?: Record<string, unknown>) => boolean
  /** Error message to show when validation fails */
  message: string
}

/**
 * Creates a Zod schema with multiple validation rules that collect all errors.
 * Use with `errorClassName="error-list-format"` in GenericForm to display as bulleted list.
 *
 * @example
 * // Define rules
 * const passwordRules: ValidationRule[] = [
 *   { test: (v) => v.length >= 12, message: 'Must be at least 12 characters' },
 *   { test: (v) => /[0-9]/.test(v), message: 'Must contain at least 1 digit' },
 *   { test: (v) => /[A-Z]/.test(v), message: 'Must contain at least 1 uppercase' },
 * ]
 *
 * // Use in form config
 * {
 *   name: 'password',
 *   zodValidation: createMultiRuleValidation(passwordRules),
 *   errorClassName: 'error-list-format',
 * }
 */
export function createMultiRuleValidation<T = string>(
  rules: ValidationRule<T>[],
  options?: {
    /** Context data passed to each rule's test function */
    context?: Record<string, unknown>
    /** Base schema to apply before rules (default: z.string()) */
    baseSchema?: z.ZodType<T>
  },
) {
  const { context = {}, baseSchema } = options || {}

  // Use provided base schema or default to string
  const schema = baseSchema || (z.string() as unknown as z.ZodType<T>)

  return schema.superRefine((val, ctx) => {
    for (const rule of rules) {
      if (!rule.test(val, context)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: rule.message,
        })
      }
    }
  })
}

/**
 * Creates multi-rule validation with dynamic context that can be updated.
 * Useful when validation depends on other form fields or external state.
 *
 * @example
 * const { getSchema, updateContext } = createDynamicMultiRuleValidation(passwordRules)
 * const schema = getSchema()
 *
 * // Update context when dependent fields change
 * updateContext({ firstName: 'John', lastName: 'Doe' })
 */
export function createDynamicMultiRuleValidation<T = string>(
  rules: ValidationRule<T>[],
  initialContext: Record<string, unknown> = {},
) {
  let context = { ...initialContext }

  const updateContext = (newContext: Record<string, unknown>) => {
    context = { ...context, ...newContext }
  }

  const getSchema = () => createMultiRuleValidation(rules, { context })

  return { getSchema, updateContext, rules }
}
